/**
 * RecommendationCard Component
 *
 * Displays a single AI-generated recommendation with priority,
 * impact level, and actionable items.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckIcon,
  TrendingDown as ReductionIcon,
  EnergySavingsLeaf as EcoIcon,
  BarChart as BenchmarkIcon,
  FactCheck as QualityIcon
} from '@mui/icons-material';
import {
  Recommendation,
  PriorityLevel,
  ImpactLevel,
  RecommendationCategory,
  getPriorityColor,
  formatReduction
} from '../../services/insightService';

interface RecommendationCardProps {
  recommendation: Recommendation;
}

const getCategoryIcon = (category: RecommendationCategory) => {
  switch (category) {
    case 'emissions':
      return <EcoIcon />;
    case 'benchmarking':
      return <BenchmarkIcon />;
    case 'data_quality':
      return <QualityIcon />;
    default:
      return <EcoIcon />;
  }
};

const getCategoryLabel = (category: RecommendationCategory): string => {
  switch (category) {
    case 'emissions':
      return 'Emission Reduction';
    case 'benchmarking':
      return 'Benchmarking';
    case 'data_quality':
      return 'Data Quality';
    default:
      return category;
  }
};

const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const priorityColor = getPriorityColor(recommendation.priority);

  const getBorderColor = () => {
    switch (recommendation.priority) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[300];
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: `4px solid ${getBorderColor()}`,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {getCategoryIcon(recommendation.category)}
            </Box>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {recommendation.title}
            </Typography>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            {recommendation.estimated_reduction_pct && (
              <Chip
                icon={<ReductionIcon sx={{ fontSize: 16 }} />}
                label={formatReduction(recommendation.estimated_reduction_pct)}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tags */}
        <Box display="flex" gap={1} mb={2}>
          <Chip
            label={getCategoryLabel(recommendation.category)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
          <Chip
            label={`${recommendation.priority.toUpperCase()} PRIORITY`}
            size="small"
            color={priorityColor}
            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
          />
          <Chip
            label={`${recommendation.impact.toUpperCase()} IMPACT`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {recommendation.description}
        </Typography>

        {/* Expandable Action Items */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {recommendation.action_items.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
              >
                Action Items
              </Typography>
              <List dense disablePadding>
                {recommendation.action_items.map((item, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon
                        sx={{ fontSize: 18, color: theme.palette.success.main }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {recommendation.affected_facility_name && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                Affected Facility: {recommendation.affected_facility_name}
              </Typography>
            </Box>
          )}

          {recommendation.affected_scope && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                Scope: {recommendation.affected_scope}
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default RecommendationCard;
